'use client';

import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react';
import { GreenhouseCustomFieldId, type GreenhouseJob } from '@/libs/greenhouse/greenhouse';
import { getJobsByGreenhouseField } from '@/libs/greenhouse/greenhouse-functions';

interface GreenhouseContextValue {
  jobs: GreenhouseJob[];
  getJob: (id: number) => GreenhouseJob | undefined;
  featuredJobs: GreenhouseJob[];
  remoteJobs: GreenhouseJob[];
}

const GreenhouseContext = createContext<GreenhouseContextValue>({
  jobs: [],
  getJob: () => undefined,
  featuredJobs: [],
  remoteJobs: [],
});

export const GreenhouseProvider = ({ children, jobs }: { children: ReactNode; jobs: GreenhouseJob[] }) => {
  const getJob = useCallback((id: number) => jobs.find(job => job.id === id), [jobs]);
  const featuredJobs = useMemo(() => getJobsByGreenhouseField(jobs, GreenhouseCustomFieldId.FeaturedJob), [jobs]);
  const remoteJobs = useMemo(() => getJobsByGreenhouseField(jobs, GreenhouseCustomFieldId.OpenToRemote), [jobs]);
  const value = useMemo(() => ({ jobs, getJob, featuredJobs, remoteJobs }), [jobs, getJob, featuredJobs, remoteJobs]);
  return <GreenhouseContext.Provider value={value}>{children}</GreenhouseContext.Provider>;
};

export const useGreenhouse = () => useContext(GreenhouseContext);
